import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const EventManagement = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSearchOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sample data
  const stats = [
    { id: 1, title: 'Total Event', value: 24, trend: 'up', icon: '📅' },
    { id: 2, title: 'Total Tickets sold', value: 1843, trend: 'up', icon: '🎟️' },
    { id: 3, title: 'Page Views', value: 12567, trend: 'down', icon: '👁️' }
  ];

  const events = {
    upcoming: [
      {
        id: 1,
        title: 'Nigeria Female Entrepreneurs Conference',
        type: 'Online event',
        day: "Apr 7",
        date: 'Sunday, October 27, 2024 at 3:00 AM',
        sold: '0/100',
        gross: '0.00',
        status: 'Draft',
        visibility: 'Public',
        image: "FestivalImage.png"
      },
      {
        id: 2,
        title: 'Tech Innovation Summit',
        type: 'In-person event',
        date: 'Friday, November 15, 2024 at 9:00 AM',
        sold: '45/200',
        day: "Apr 7",
        gross: '2,250.00',
        status: 'Published',
        visibility: 'Public',
        image: "FestivalImage.png"
      }
    ],
    past: [
      {
        id: 3,
        title: 'Digital Marketing Workshop',
        type: 'Online event',
        date: 'Saturday, August 10, 2024 at 2:00 PM',
        sold: '120/120',
        day: "Apr 7",
        gross: '3,600.00',
        status: 'Completed',
        visibility: 'Public',
        image: "FestivalImage.png"
      }
    ],
    draft: [
      {
        id: 4,
        title: 'Annual Music Festival',
        type: 'In-person event',
        date: 'Saturday, December 28, 2024 at 6:00 PM',
        sold: '0/500',
        day: "Apr 7",
        gross: '0.00',
        status: 'Draft',
        visibility: 'Private',
        image: "FestivalImage.png"
      }
    ]
  };

  const filteredEvents = events[activeTab].filter(event => 
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 bg-[#F5F5F5] min-h-screen overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-gray-800">Event Management</h1>
          <Link to="/dashboard/event-management/create-event">
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className='bg-[#FCD20B] h-12 md:h-[64px] px-4 md:w-[208px] rounded-[32px] text-sm md:text-[18px] font-medium flex items-center justify-center shadow-sm'
            >
              <span className="mr-2">+</span> Create Event
            </motion.button>
          </Link>
        </motion.div>

        {/* Stats Cards */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.id}
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: stat.id * 0.1 }}
              className="bg-white rounded-xl shadow-sm p-5 border border-gray-100"
            >
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 text-2xl">{stat.icon}</div>
                <div className="flex-1">
                  <h3 className="text-sm text-gray-500 font-medium">{stat.title}</h3>
                  <p className="text-2xl mt-1 font-bold">{stat.value.toLocaleString()}</p>
                </div>
                <div className={`flex-shrink-0 ${stat.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                  {stat.trend === 'up' ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Tabs and Search */}
        <div className="flex flex-col md:flex-row justify-between mb-6 gap-4">
          <div className='bg-white px-4 rounded-[8px] flex-grow md:flex-grow-0 md:w-auto shadow-sm'>
            <div className="flex justify-between h-[60px] overflow-x-auto scrollbar-thin">
              {['upcoming', 'past', 'draft'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 md:px-4 py-2 text-sm md:text-[18px] font-normal relative whitespace-nowrap ${
                    activeTab === tab 
                      ? 'text-white bg-[#06044B] h-[40px] my-auto rounded-[10px] md:w-[274px]' 
                      : 'text-[#7F7F7F] hover:text-gray-700'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} events
                  <span className={`ml-1 md:ml-2 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs ${
                    activeTab === tab ? 'bg-white text-[#06044B]' : 'bg-gray-200 text-[#7F7F7F]'
                  }`}>
                    {events[tab].length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Search - Desktop */}
          <div className="relative hidden md:block">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.0203 10.0777L13.8757 12.9323L12.9323 13.8757L10.0777 11.0203C9.0155 11.8718 7.69434 12.3349 6.33301 12.333C3.02101 12.333 0.333008 9.64501 0.333008 6.33301C0.333008 3.02101 3.02101 0.333008 6.33301 0.333008C9.64501 0.333008 12.333 3.02101 12.333 6.33301C12.3349 7.69434 11.8718 9.0155 11.0203 10.0777ZM9.68301 9.58301C10.5289 8.71281 11.0014 7.54659 10.9997 6.33301C10.9997 3.75501 8.91101 1.66634 6.33301 1.66634C3.75501 1.66634 1.66634 3.75501 1.66634 6.33301C1.66634 8.91101 3.75501 10.9997 6.33301 10.9997C7.54659 11.0014 8.71281 10.5289 9.58301 9.68301L9.68301 9.58301Z" fill="#888888"/>
              </svg>
            </div>
            <input
              type="search"
              placeholder="Search Events..."
              className="pl-10 w-full md:w-[250px] h-[48px] md:h-[64px] rounded-[50px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Search - Mobile */}
          <div className="md:hidden flex items-center">
            {isSearchOpen ? (
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.0203 10.0777L13.8757 12.9323L12.9323 13.8757L10.0777 11.0203C9.0155 11.8718 7.69434 12.3349 6.33301 12.333C3.02101 12.333 0.333008 9.64501 0.333008 6.33301C0.333008 3.02101 3.02101 0.333008 6.33301 0.333008C9.64501 0.333008 12.333 3.02101 12.333 6.33301C12.3349 7.69434 11.8718 9.0155 11.0203 10.0777ZM9.68301 9.58301C10.5289 8.71281 11.0014 7.54659 10.9997 6.33301C10.9997 3.75501 8.91101 1.66634 6.33301 1.66634C3.75501 1.66634 1.66634 3.75501 1.66634 6.33301C1.66634 8.91101 3.75501 10.9997 6.33301 10.9997C7.54659 11.0014 8.71281 10.5289 9.58301 9.68301L9.68301 9.58301Z" fill="#888888"/>
                  </svg>
                </div>
                <input
                  type="search"
                  placeholder="Search Events..."
                  className="pl-10 w-full h-[48px] rounded-[50px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button 
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setIsSearchOpen(false)}
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setIsSearchOpen(true)}
                className="p-2 bg-white rounded-full shadow-sm"
              >
                <svg width="20" height="20" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.0203 10.0777L13.8757 12.9323L12.9323 13.8757L10.0777 11.0203C9.0155 11.8718 7.69434 12.3349 6.33301 12.333C3.02101 12.333 0.333008 9.64501 0.333008 6.33301C0.333008 3.02101 3.02101 0.333008 6.33301 0.333008C9.64501 0.333008 12.333 3.02101 12.333 6.33301C12.3349 7.69434 11.8718 9.0155 11.0203 10.0777ZM9.68301 9.58301C10.5289 8.71281 11.0014 7.54659 10.9997 6.33301C10.9997 3.75501 8.91101 1.66634 6.33301 1.66634C3.75501 1.66634 1.66634 3.75501 1.66634 6.33301C1.66634 8.91101 3.75501 10.9997 6.33301 10.9997C7.54659 11.0014 8.71281 10.5289 9.58301 9.68301L9.68301 9.58301Z" fill="#888888"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Events List - Desktop View */}
        <div className="bg-white shadow-sm overflow-hidden border border-gray-100 rounded-xl hidden md:block">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 text-sm p-4 border-b border-gray-200 font-semibold text-[#1E1E1E]">
            <div className="col-span-2">Date</div>
            <div className="col-span-4">Event</div>
            <div className="col-span-1">Sold</div>
            <div className="col-span-1">Gross</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Visibility</div>
          </div>

          <AnimatePresence mode="wait">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50"
                >
                  <div className="grid grid-cols-12 gap-4 p-4 items-center">
                    {/* Date Column */}
                    <div className="col-span-2">
                      <p className="text-sm md:text-[18px] font-normal text-[#7F7F7F]">{event.day}</p>
                    </div>
                    
                    {/* Event Column */}
                    <div className="col-span-4 flex items-center space-x-3">
                      <img 
                        src={event.image} 
                        alt={event.title} 
                        className="w-10 h-10 rounded-md object-cover"
                      />
                      <div>
                        <h3 className="font-semibold text-sm md:text-[18px] text-[#1E1E1E] line-clamp-1">{event.title}</h3>
                        <p className="text-xs md:text-[16px] font-normal text-[#7F7F7F]">{event.type}</p>
                        <p className="text-xs md:text-[16px] font-normal text-[#7F7F7F] line-clamp-1">{event.date}</p>
                      </div>
                    </div>
                    
                    {/* Sold Column */}
                    <div className="col-span-1">
                      <p className="text-xs md:text-[16px] font-normal text-[#7F7F7F]">{event.sold}</p>
                    </div>
                    
                    {/* Gross Column */}
                    <div className="col-span-1">
                      <p className="text-xs md:text-[16px] font-normal text-[#7F7F7F]">${event.gross}</p>
                    </div>
                    
                    {/* Status Column */}
                    <div className="col-span-2">
                      <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${
                        event.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                        event.status === 'Published' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {event.status}
                      </span>
                    </div>
                    
                    {/* Visibility Column */}
                    <div className="col-span-2">
                      <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${
                        event.visibility === 'Public' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {event.visibility}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                No events found matching your search.
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Events List - Mobile View */}
        <div className="md:hidden space-y-4">
          <AnimatePresence mode="wait">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 p-4"
                >
                  <div className="flex items-start space-x-3">
                    <img 
                      src={event.image} 
                      alt={event.title} 
                      className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold text-[#1E1E1E] line-clamp-1">{event.title}</h3>
                        <p className="text-sm font-normal text-[#7F7F7F]">{event.day}</p>
                      </div>
                      
                      <p className="text-sm font-normal text-[#7F7F7F] mt-1">{event.type}</p>
                      <p className="text-sm font-normal text-[#7F7F7F] line-clamp-1">{event.date}</p>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        <div className="flex items-center text-sm text-[#7F7F7F]">
                          <span className="font-medium">Sold:</span>
                          <span className="ml-1">{event.sold}</span>
                        </div>
                        <div className="flex items-center text-sm text-[#7F7F7F]">
                          <span className="font-medium">Gross:</span>
                          <span className="ml-1">${event.gross}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${
                          event.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                          event.status === 'Published' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {event.status}
                        </span>
                        
                        <span className={`px-2 py-1 inline-flex text-xs font-medium rounded-full ${
                          event.visibility === 'Public' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {event.visibility}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500 bg-white rounded-xl shadow-sm">
                No events found matching your search.
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex justify-center">
          <nav className="inline-flex shadow-sm rounded-md overflow-hidden">
            <button className="px-3 py-1 bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50" disabled>
              Previous
            </button>
            <button className="px-3 py-1 bg-indigo-600 border border-indigo-600 text-sm font-medium text-white hover:bg-indigo-700">
              1
            </button>
            <button className="px-3 py-1 bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50">
              2
            </button>
            <button className="px-3 py-1 bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50">
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default EventManagement;